import React from 'react';
import { Paper } from '@mui/material';
import { Chip } from '../chip';
import type { DesktopBuildProfit } from '../../utils/desktopBuildProfit';
import './DesktopBuildProfitSummary.css';

/**
 * Internal money summary at the top of the New Desktop (Custom) build sheet:
 * customer cost, our cost, shipping and profit on the parts. Build labour
 * (RCS.* lines) is left out. Never rendered on the customer preview or the
 * invoice — buy price and shipping are internal.
 */

interface DesktopBuildProfitSummaryProps {
  profit: DesktopBuildProfit;
  gstSuffix: string;
}

const money = (n: number) =>
  `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const DesktopBuildProfitSummary: React.FC<DesktopBuildProfitSummaryProps> = ({
  profit,
  gstSuffix,
}) => {
  const tiles: { label: string; value: number; emphasis?: boolean }[] = [
    { label: 'Customer cost', value: profit.customerCost },
    { label: 'Our cost', value: profit.ourCost },
    { label: 'Shipping', value: profit.shipping },
    { label: 'Our profit', value: profit.profit, emphasis: true },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: '8px' }} aria-label="Build profit">
      <div className="build-profit__grid">
        {tiles.map((t) => (
          <div
            key={t.label}
            className={`build-profit__tile${t.emphasis ? ' build-profit__tile--profit' : ''}`}
          >
            <span className="build-profit__label">{t.label}</span>
            <span className="build-profit__value">
              {money(t.value)}
              {t.emphasis && t.value < 0 && <Chip tone="error">Loss</Chip>}
            </span>
          </div>
        ))}
      </div>
      <div className="build-profit__caption">
        Parts only, {gstSuffix}, before the EFT fee
        {profit.excludedLineCount > 0 &&
          ` — ${profit.excludedLineCount} build labour line${profit.excludedLineCount === 1 ? '' : 's'} (RCS.*) not counted`}
        . Internal — not shown to the customer.
      </div>
    </Paper>
  );
};
